import React from 'react';
import { Shield, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const PrivacyPolicy = () => {
    const navigate = useNavigate();
    const { language } = useLanguage();

    const content = {
        en: {
            title: "Privacy Policy",
            lastUpdated: "Last Updated: February 2026",
            sections: [
                {
                    title: "1. Information We Collect",
                    content: `We collect the following information to provide our services:
• Phone number for authentication
• Business name and owner name (optional)
• Transaction data (sales, expenses, amounts, categories)
• Payment information received via UPI/Razorpay webhooks
• Device tokens for push notifications (optional)`
                },
                {
                    title: "2. How We Use Your Data",
                    content: `Your data is used to:
• Authenticate and secure your account
• Process and display your transaction history
• Analyze transactions for potential fraud using AI
• Send push notifications for high-risk alerts
• Generate daily, weekly, and monthly reports
• Improve our services`
                },
                {
                    title: "3. Data Storage & Security",
                    content: `• All data is stored securely in encrypted databases
• We use industry-standard security practices
• Your data is never sold to third parties
• Payment data is processed securely via Razorpay
• We retain data only as long as you use our services`
                },
                {
                    title: "4. Third-Party Services",
                    content: `We use the following third-party services:
• Supabase - Authentication
• Razorpay - Payment processing
• Google Gemini AI - Transaction risk analysis
• Firebase - Push notifications
These services have their own privacy policies.`
                },
                {
                    title: "5. Your Rights",
                    content: `You have the right to:
• Access your data
• Request data deletion
• Update your information
• Opt-out of push notifications
• Export your transaction data`
                },
                {
                    title: "6. Contact Us",
                    content: `For privacy-related questions or data requests, please contact us through the app's support section.`
                }
            ]
        },
        hi: {
            title: "गोपनीयता नीति",
            lastUpdated: "अंतिम अपडेट: फरवरी 2026",
            sections: [
                {
                    title: "1. हम क्या जानकारी एकत्र करते हैं",
                    content: `हम अपनी सेवाएं प्रदान करने के लिए निम्नलिखित जानकारी एकत्र करते हैं:
• प्रमाणीकरण के लिए फोन नंबर
• व्यवसाय का नाम और मालिक का नाम (वैकल्पिक)
• लेन-देन डेटा (बिक्री, खर्च, राशि, श्रेणियां)
• UPI/Razorpay वेबहुक से प्राप्त भुगतान जानकारी
• पुश नोटिफिकेशन के लिए डिवाइस टोकन (वैकल्पिक)`
                },
                {
                    title: "2. हम आपके डेटा का उपयोग कैसे करते हैं",
                    content: `आपके डेटा का उपयोग किया जाता है:
• आपके खाते को प्रमाणित और सुरक्षित करने के लिए
• आपके लेन-देन इतिहास को प्रोसेस और प्रदर्शित करने के लिए
• AI का उपयोग करके संभावित धोखाधड़ी के लिए लेन-देन का विश्लेषण करने के लिए
• उच्च जोखिम अलर्ट के लिए पुश नोटिफिकेशन भेजने के लिए
• दैनिक, साप्ताहिक और मासिक रिपोर्ट जेनरेट करने के लिए`
                },
                {
                    title: "3. डेटा स्टोरेज और सुरक्षा",
                    content: `• सभी डेटा एन्क्रिप्टेड डेटाबेस में सुरक्षित रूप से संग्रहीत है
• हम उद्योग-मानक सुरक्षा प्रथाओं का उपयोग करते हैं
• आपका डेटा कभी भी तीसरे पक्ष को नहीं बेचा जाता
• भुगतान डेटा Razorpay के माध्यम से सुरक्षित रूप से संसाधित होता है`
                },
                {
                    title: "4. तृतीय-पक्ष सेवाएं",
                    content: `हम निम्नलिखित तृतीय-पक्ष सेवाओं का उपयोग करते हैं:
• Supabase - प्रमाणीकरण
• Razorpay - भुगतान प्रोसेसिंग
• Google Gemini AI - लेन-देन जोखिम विश्लेषण
• Firebase - पुश नोटिफिकेशन`
                },
                {
                    title: "5. आपके अधिकार",
                    content: `आपको अधिकार है:
• अपने डेटा तक पहुंचने का
• डेटा हटाने का अनुरोध करने का
• अपनी जानकारी अपडेट करने का
• पुश नोटिफिकेशन से ऑप्ट-आउट करने का`
                },
                {
                    title: "6. संपर्क करें",
                    content: `गोपनीयता संबंधी प्रश्नों के लिए, कृपया ऐप के सपोर्ट सेक्शन के माध्यम से हमसे संपर्क करें।`
                }
            ]
        }
    };

    const t = content[language] || content.en;

    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-3xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(-1)}
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Shield className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">{t.title}</h1>
                            <p className="text-sm text-muted-foreground">{t.lastUpdated}</p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="space-y-6">
                    {t.sections.map((section, index) => (
                        <Card key={index}>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-lg">{section.title}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-muted-foreground whitespace-pre-line">
                                    {section.content}
                                </p>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Footer */}
                <div className="mt-8 text-center text-sm text-muted-foreground">
                    <p>ShieldPayments © 2026. All rights reserved.</p>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
